import type { ReactNode } from "react";
import {
  MapIcon,
  RocketIcon,
  CodeIcon,
  GraduationCapIcon,
  CreditCardIcon,
  HistoryIcon,
  MousePointerIcon,
  AsteriskIcon,
  WavesIcon,
  PlugIcon,
} from "zudoku/icons";

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  map: MapIcon,
  rocket: RocketIcon,
  code: CodeIcon,
  "graduation-cap": GraduationCapIcon,
  "credit-card": CreditCardIcon,
  history: HistoryIcon,
  "mouse-pointer": MousePointerIcon,
  asterisk: AsteriskIcon,
  waves: WavesIcon,
  plug: PlugIcon,
};

export function Icon({ name, size = 18 }: { name: string; size?: number }) {
  const LucideIcon = iconMap[name];
  if (!LucideIcon) return null;
  return <LucideIcon size={size} className="shrink-0" />;
}

export function CardGroup({
  cols = 2,
  children,
}: {
  cols?: number;
  children: ReactNode;
}) {
  return (
    <div
      className="grid gap-4 my-6 not-prose"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {children}
    </div>
  );
}


export function AccordionGroup({ children }: { children: ReactNode }) {
  return <div className="space-y-2 my-4 not-prose">{children}</div>;
}

export function Accordion({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <details className="group border rounded-lg">
      <summary className="cursor-pointer font-medium p-4 select-none list-none flex items-center justify-between">
        {title}
        <svg
          className="w-4 h-4 transition-transform group-open:rotate-180"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </summary>
      <div className="px-4 pb-4 text-[var(--color-muted-foreground)] text-sm">
        {children}
      </div>
    </details>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950/30 p-4 my-4 rounded-r-lg not-prose">
      <p className="text-sm m-0">{children}</p>
    </div>
  );
}

export function Steps({ children }: { children: ReactNode }) {
  return (
    <div className="my-6 space-y-6 ml-4 border-l-2 border-[var(--color-primary)]/30 pl-6 not-prose">
      {children}
    </div>
  );
}

export function Step({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <div className="absolute -left-[calc(1.5rem+5px)] top-1.5 w-2.5 h-2.5 rounded-full bg-[var(--color-primary)]" />
      <h4 className="font-semibold text-base mb-1">{title}</h4>
      <div className="text-sm text-[var(--color-muted-foreground)]">
        {children}
      </div>
    </div>
  );
}
